import { Injectable, NgZone } from '@angular/core';
import { HttpBackend, HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, catchError, map, throwError } from 'rxjs';

import constant from '../../app.constant';
import { LocalStorageService } from '../../utils/local-storage.service';
import {
    CONTROLX_DEFAULT_LIMIT,
    ControlxActionPayload,
    ControlxActivity,
    ControlxActivityQuery,
    ControlxActivityResponse,
    ControlxActivitySnapshot,
    ControlxError,
    ControlxStreamEvent,
    ControlxStreamQuery,
    controlxError
} from './controlx-activity.model';

/**
 * ControlX Activity Feed — REST snapshot + live SSE stream.
 *
 * Order Detail is the first consumer, but nothing here knows about it: pass an
 * `orderId` (or a `tripId`, or neither for the tenant-wide slice) and the same
 * service serves any screen. Tenant/role scoping is the server's job and is
 * driven entirely by the token — this client never sends, and must never send,
 * a scope of its own.
 *
 * Two deliberate choices worth knowing about:
 *
 * 1. Requests go through a bare `HttpClient` built on `HttpBackend`, i.e. they
 *    skip the app interceptor chain, and the JWT is attached here instead.
 *    `ErrorInterceptor` rewrites every failure into a bare message string and
 *    logs the user out of the whole platform on a 401. Both are wrong for this
 *    panel: the UI has to tell 401 from 403 from 404 to show the right state
 *    (the spec gives each a distinct meaning), and a hiccup in a secondary,
 *    third-origin feed must never end the user's session on the main app. The
 *    token still comes from the one place the platform keeps it.
 *
 * 2. Reconnection is left to `EventSource`. It replays `Last-Event-ID` on its
 *    own and the server backfills from it, so hand-rolled catch-up would only
 *    duplicate work the spec tells us not to do. What is added is the stop
 *    condition the browser lacks: `EventSource` otherwise retries an expired
 *    token forever. The one gap `EventSource` cannot cover itself is a
 *    connection that goes silently dead without the transport ever reporting
 *    an error — `readyState` stays `OPEN` with nothing arriving. An idle
 *    watchdog (`STREAM_IDLE_TIMEOUT_MS`) forces a manual reconnect when no
 *    frame has arrived for that long. Every failure path — a `CLOSED` error,
 *    a `CONNECTING` drop, or the idle watchdog — retries the same way: close,
 *    wait `STREAM_RECONNECT_DELAY_MS`, reconnect, up to `MAX_STREAM_RETRIES`.
 */
@Injectable({ providedIn: 'root' })
export class ControlxActivityService {

    /** Interceptor-free client — see the class note. */
    private readonly http: HttpClient;

    /**
     * Consecutive reconnect attempts that never reached `open` before the
     * stream is given up on. Transport blips recover well inside this; a
     * server that refuses us does not, and would otherwise loop forever.
     */
    private readonly MAX_STREAM_RETRIES = 6;

    /**
     * Milliseconds of silence (no frame of any kind, including a fresh
     * `open`) before a connection the browser still reports as open is
     * treated as dead and force-reconnected.
     *
     * `EventSource` only notices a drop that the transport actually surfaces
     * (a reset, a proper close). A proxy or load balancer that silently stops
     * forwarding bytes leaves `readyState` at `OPEN` forever with nothing
     * ever arriving again — this watchdog is the only thing that catches that.
     *
     * Verified against the live stream: a quiet order can go well past 30s
     * with no frame of any kind (ControlX's keep-alive isn't something
     * `EventSource` can see), so the threshold has to sit above normal quiet
     * periods or this fires on healthy connections. 2 minutes is a deliberate
     * margin above that, kept only as a last-resort net for a truly dead link.
     */
    private readonly STREAM_IDLE_TIMEOUT_MS = 120000;

    /** Pause before every reconnect attempt — idle timeout, `CLOSED`, or `CONNECTING`. */
    private readonly STREAM_RECONNECT_DELAY_MS = 500;

    constructor(
        backend: HttpBackend,
        private localStorage: LocalStorageService,
        private zone: NgZone
    ) {
        this.http = new HttpClient(backend);
    }

    /** False when the build has no ControlX origin configured; callers should stay silent. */
    get isConfigured(): boolean {
        return !!constant.Service.ControlX.BaseUrl;
    }

    /**
     * Snapshot of recent activity, newest first.
     *
     * With `orderId` the response covers the order's full history and carries
     * the `watching` block; without it, today's tenant-visible slice.
     */
    getActivity(query: ControlxActivityQuery = {}): Observable<ControlxActivitySnapshot> {
        if (!this.isConfigured) {
            return throwError(() => controlxError('unknown', 0, 'ControlX base URL is not configured'));
        }

        let params = new HttpParams().set('limit', String(query.limit ?? CONTROLX_DEFAULT_LIMIT));
        if (this.hasValue(query.orderId)) params = params.set('order_id', String(query.orderId));
        if (this.hasValue(query.tripId)) params = params.set('trip_id', String(query.tripId));
        if (query.prefix) params = params.set('prefix', query.prefix);

        const token = this.getToken();
        if (!token) {
            return throwError(() => controlxError('unauthorized', 401, 'No session token available'));
        }

        return this.http
            .get<ControlxActivityResponse>(
                constant.Service.ControlX.BaseUrl + constant.Service.ControlX.Activity,
                { params, headers: { 'x-access-token': token } }
            )
            .pipe(
                map(response => this.toSnapshot(response)),
                catchError((error: unknown) => throwError(() => this.toControlxError(error)))
            );
    }

    /**
     * Live activity stream.
     *
     * Cold and one-connection-per-subscription: the `EventSource` opens on
     * subscribe and is closed by the teardown, so unsubscribing (or a
     * `takeUntil` on component destroy) is all that is needed to release it.
     * Subscribe once — a second subscription is a second connection.
     *
     * Completes never; it errors with a `ControlxError` when the connection is
     * gone for good, which is the caller's cue to show a disconnected state.
     */
    streamActivity(query: ControlxStreamQuery = {}): Observable<ControlxStreamEvent> {
        return new Observable<ControlxStreamEvent>(observer => {
            if (!this.isConfigured) {
                observer.error(controlxError('unknown', 0, 'ControlX base URL is not configured'));
                return;
            }

            const token = this.getToken();
            if (!token) {
                observer.error(controlxError('unauthorized', 401, 'No session token available'));
                return;
            }

            let source: EventSource | null = null;
            let torndown = false;
            let everOpened = false;
            let failedAttempts = 0;
            let idleTimer: ReturnType<typeof setTimeout> | null = null;
            let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

            // EventSource callbacks fire outside Angular's zone, so every emission
            // is bounced back in — otherwise the rail would update its data and
            // never repaint.
            const emit = (event: ControlxStreamEvent) => {
                if (torndown) return;
                this.zone.run(() => observer.next(event));
            };

            const fail = (error: ControlxError) => {
                if (torndown) return;
                clearIdleTimer();
                clearReconnectTimer();
                teardown();
                this.zone.run(() => observer.error(error));
            };

            const clearIdleTimer = () => {
                if (!idleTimer) return;
                clearTimeout(idleTimer);
                idleTimer = null;
            };

            const clearReconnectTimer = () => {
                if (!reconnectTimer) return;
                clearTimeout(reconnectTimer);
                reconnectTimer = null;
            };

            // Any frame — open or data — proves the connection is still really
            // alive, so it pushes the deadline back out.
            const resetIdleTimer = () => {
                clearIdleTimer();
                idleTimer = setTimeout(onIdleTimeout, this.STREAM_IDLE_TIMEOUT_MS);
            };

            // Every failure that calls for a brand-new EventSource — a `CLOSED`
            // rejection or the idle watchdog — funnels through here: close, wait
            // STREAM_RECONNECT_DELAY_MS, reconnect, capped by MAX_STREAM_RETRIES
            // so a link that keeps failing this way still gives up eventually.
            // A `CONNECTING` drop is deliberately not routed through this (see
            // `onerror`): the browser's own retry resends Last-Event-ID, which a
            // needless new EventSource here would throw away for no reason.
            const scheduleReconnect = (giveUpMessage: string) => {
                failedAttempts += 1;
                if (failedAttempts >= this.MAX_STREAM_RETRIES) {
                    fail(controlxError('network', 0, giveUpMessage));
                    return;
                }
                closeSource();
                emit({ kind: 'reconnecting' });
                reconnectTimer = setTimeout(connect, this.STREAM_RECONNECT_DELAY_MS);
            };

            // The transport never told us anything was wrong, but nothing has
            // arrived in STREAM_IDLE_TIMEOUT_MS either.
            const onIdleTimeout = () => {
                if (torndown) return;
                scheduleReconnect('ControlX stream could not be re-established');
            };

            const onActivity = (event: MessageEvent) => {
                resetIdleTimer();
                const entry = this.parseJson<ControlxActivity>(event?.data);
                if (entry) emit({ kind: 'activity', entry });
            };

            const onReassign = (event: MessageEvent) => {
                resetIdleTimer();
                const payload = this.parseJson<ControlxActionPayload>(event?.data);
                if (payload) emit({ kind: 'action', action: 'reassign', payload });
            };

            const onCancel = (event: MessageEvent) => {
                resetIdleTimer();
                const payload = this.parseJson<ControlxActionPayload>(event?.data);
                if (payload) emit({ kind: 'action', action: 'cancel', payload });
            };

            // Closes and detaches the current EventSource only — used both for
            // final teardown and for a manual reconnect, which must not touch
            // `torndown` (the observable itself is still very much alive).
            const closeSource = () => {
                if (!source) return;
                source.removeEventListener('activity', onActivity as EventListener);
                source.removeEventListener('re-assign', onReassign as EventListener);
                source.removeEventListener('Cancel', onCancel as EventListener);
                source.onopen = null;
                source.onmessage = null;
                source.onerror = null;
                source.close();
                source = null;
            };

            const teardown = () => {
                if (torndown) return;
                torndown = true;
                clearIdleTimer();
                clearReconnectTimer();
                closeSource();
            };

            const connect = () => {
                resetIdleTimer();
                source = new EventSource(this.buildStreamUrl(query, token));

                source.onopen = () => {
                    resetIdleTimer();
                    const reconnected = everOpened;
                    everOpened = true;
                    failedAttempts = 0;
                    emit({ kind: 'open', reconnected });
                };

                // Activity frames are accepted both ways: `onmessage` catches an
                // unnamed frame, the listener catches `event: activity`. A frame is
                // one or the other, never both, so this cannot double-deliver — and
                // getting it wrong is silent, the connection stays open and healthy
                // while every row is dropped, which looks exactly like a feed that
                // only updates on refresh.
                source.onmessage = onActivity;
                source.addEventListener('activity', onActivity as EventListener);
                // Named triggers, exact casing per the spec.
                source.addEventListener('re-assign', onReassign as EventListener);
                source.addEventListener('Cancel', onCancel as EventListener);

                source.onerror = () => {
                    if (torndown) return;

                    // CLOSED here means the browser has given up retrying this
                    // EventSource itself: a non-2xx response, the wrong content
                    // type, or a CORS/auth rejection. That doesn't mean the
                    // underlying condition is permanent — a proxy/LB can close a
                    // connection outright for reasons that clear up on the next
                    // attempt — so it gets the same fast manual retry as the idle
                    // watchdog rather than being treated as terminal.
                    //
                    // Reported as `network` even when it is really a 401, because
                    // EventSource exposes no status code and guessing would tell a
                    // user to sign in again over what may be a CORS problem. The
                    // snapshot call runs first and can read the status, so auth
                    // state is established there and only there.
                    if (!source || source.readyState === EventSource.CLOSED) {
                        scheduleReconnect(everOpened
                            ? 'ControlX stream closed by the server'
                            : 'ControlX stream could not be established');
                        return;
                    }

                    // CONNECTING: a transient drop the browser is already retrying
                    // on its own, resending Last-Event-ID so the server replays
                    // what was missed. Left alone rather than routed through
                    // `scheduleReconnect` — a manual new EventSource here would
                    // throw that replay away for nothing. Only the give-up budget
                    // is shared, so this still stops looping forever.
                    failedAttempts += 1;
                    if (failedAttempts >= this.MAX_STREAM_RETRIES) {
                        fail(controlxError('network', 0, 'ControlX stream could not be re-established'));
                        return;
                    }
                    emit({ kind: 'reconnecting' });
                };
            };

            connect();

            return teardown;
        });
    }

    /**
     * The stream URL.
     *
     * `EventSource` cannot set headers, so the spec accepts the JWT in the query
     * string here and only here; every other call uses `x-access-token`. The URL
     * is built at subscribe time and never stored or logged.
     */
    private buildStreamUrl(query: ControlxStreamQuery, token: string): string {
        const params = new URLSearchParams();
        params.set('token', token);
        if (this.hasValue(query.orderId)) params.set('order_id', String(query.orderId));
        if (this.hasValue(query.tripId)) params.set('trip_id', String(query.tripId));
        if (query.source) params.set('source', String(query.source));
        return `${constant.Service.ControlX.BaseUrl}${constant.Service.ControlX.ActivityStream}?${params.toString()}`;
    }

    /** The platform JWT, from the single place the app stores it. */
    private getToken(): string {
        const token = this.localStorage.getItem(constant.localStorage.token);
        return typeof token === 'string' && token.trim() ? token.trim() : '';
    }

    private hasValue(value: unknown): boolean {
        return value !== null && value !== undefined && String(value).trim() !== '';
    }

    private toSnapshot(response: ControlxActivityResponse | null | undefined): ControlxActivitySnapshot {
        const data = Array.isArray(response?.data) ? response!.data! : [];
        return {
            entries: data.filter((entry): entry is ControlxActivity => !!entry && typeof entry === 'object'),
            watching: response?.watching ?? null
        };
    }

    private parseJson<T>(raw: unknown): T | null {
        if (typeof raw !== 'string' || !raw.trim()) return null;
        try {
            const parsed = JSON.parse(raw);
            return parsed && typeof parsed === 'object' ? (parsed as T) : null;
        } catch {
            // A producer sending a frame we cannot read must not kill the stream.
            return null;
        }
    }

    /** Maps transport failures onto the states the UI distinguishes. */
    private toControlxError(error: unknown): ControlxError {
        if (!(error instanceof HttpErrorResponse)) {
            return controlxError('unknown', 0, 'ControlX request failed');
        }
        switch (error.status) {
            case 401:
                return controlxError('unauthorized', 401, 'ControlX session is no longer valid');
            case 403:
                // Spec: non-admin callers need the ControlX addon activated.
                return controlxError('forbidden', 403, 'ControlX is not enabled for this account');
            case 404:
                // Spec: deliberately 404 rather than 403, so order ids cannot be probed.
                return controlxError('not-found', 404, 'No ControlX visibility for this record');
            case 0:
                return controlxError('network', 0, 'ControlX could not be reached');
            default:
                return controlxError('unknown', error.status, error.message || 'ControlX request failed');
        }
    }
}
